import React, { useState, useEffect, useCallback, useRef } from 'react';
import imageGeneratorService from '../services/imageGeneratorService';
import './ImageGenerator.css';

const ImageGenerator = ({ thought, author, onImageGenerated, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [shouldRegenerate, setShouldRegenerate] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageSource, setImageSource] = useState('generate'); // 'generate' or 'upload'
  const fileInputRef = useRef(null);

  const handleGenerateImage = useCallback(async () => {
    if (!thought) {
      setError('Please enter a thought first');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await imageGeneratorService.generateThoughtImage(thought, author);
      
      if (result.success) {
        setGeneratedImage(result);
        setPreviewMode(true);
      } else {
        setError(result.error || 'Failed to generate image. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while generating the image. Please try again.');
      console.error('Image generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [thought, author]);

  // Handle regeneration when shouldRegenerate flag is set
  useEffect(() => {
    if (shouldRegenerate && !isGenerating) {
      setShouldRegenerate(false);
      handleGenerateImage();
    }
  }, [shouldRegenerate, isGenerating, handleGenerateImage]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  const handleUseImage = () => {
    if (generatedImage && onImageGenerated) {
      onImageGenerated(generatedImage.imageUrl);
    }
    onClose();
  };

  const handleRegenerate = () => {
    setGeneratedImage(null);
    setUploadedImage(null);
    setPreviewMode(false);
    setError(null);
    setIsGenerating(false);
    setShouldRegenerate(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, GIF, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setUploadedImage({
        file: file,
        url: imageUrl,
        name: file.name
      });
      setPreviewMode(true);
    };
    reader.readAsDataURL(file);
  };

  const handleUseUploadedImage = () => {
    if (uploadedImage && onImageGenerated) {
      onImageGenerated(uploadedImage.url);
    }
    onClose();
  };

  const handleSourceChange = (source) => {
    setImageSource(source);
    setError(null);
    setGeneratedImage(null);
    setUploadedImage(null);
    setPreviewMode(false);
    setIsGenerating(false);
    setShouldRegenerate(false);
  };

  const handleChangeImage = () => {
    setUploadedImage(null);
    setPreviewMode(false);
    setError(null);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="image-generator-overlay" onClick={handleOverlayClick}>
      <div className="image-generator-modal">
        <div className="image-generator-header">
          <h3>AI Image Generator</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="image-generator-content">
          {!previewMode ? (
            <div className="generator-form">
              <div className="input-group">
                <label>Thought:</label>
                <textarea 
                  value={thought || ''} 
                  readOnly 
                  className="thought-preview"
                  placeholder="Enter your thought here..."
                />
              </div>
              
              <div className="input-group">
                <label>Author:</label>
                <input 
                  type="text" 
                  value={author || ''} 
                  readOnly 
                  className="author-preview"
                  placeholder="Author name"
                />
              </div>

              <div className="input-group">
                <label>Image Source:</label>
                <div className="source-options">
                  <label className="source-option">
                    <input
                      type="radio"
                      name="imageSource"
                      value="generate"
                      checked={imageSource === 'generate'}
                      onChange={(e) => handleSourceChange(e.target.value)}
                    />
                    <span>🎨 Generate AI Image</span>
                  </label>
                  <label className="source-option">
                    <input
                      type="radio"
                      name="imageSource"
                      value="upload"
                      checked={imageSource === 'upload'}
                      onChange={(e) => handleSourceChange(e.target.value)}
                    />
                    <span>📁 Upload from Computer</span>
                  </label>
                </div>
              </div>

              {imageSource === 'upload' && (
                <div className="input-group">
                  <label>Select Image File:</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="file-input"
                    id="imageUpload"
                  />
                  <label htmlFor="imageUpload" className="file-input-label">
                    📁 Choose Image File
                  </label>
                  <p className="file-input-help">
                    Supported formats: PNG, JPG, GIF, WebP (Max 10MB)
                  </p>
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="generator-actions">
                {imageSource === 'generate' && (
                  <button 
                    onClick={handleGenerateImage}
                    disabled={isGenerating || !thought}
                    className="generate-btn"
                  >
                    {isGenerating ? 'Generating...' : 'Generate Image'}
                  </button>
                )}
                <button onClick={onClose} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={onClose} className="close-btn-secondary">
                  ✕ Close
                </button>
              </div>
            </div>
          ) : (
            <div className="image-preview">
              <div className="preview-header">
                <h4>{imageSource === 'generate' ? 'Generated Image Preview' : 'Uploaded Image Preview'}</h4>
                <div className="preview-actions">
                  {imageSource === 'generate' && (
                    <button onClick={handleRegenerate} className="regenerate-btn">
                      Regenerate
                    </button>
                  )}
                  {imageSource === 'upload' && (
                    <button onClick={handleChangeImage} className="regenerate-btn">
                      Change Image
                    </button>
                  )}
                  <button 
                    onClick={imageSource === 'generate' ? handleUseImage : handleUseUploadedImage} 
                    className="use-btn"
                  >
                    Use This Image
                  </button>
                  <button onClick={onClose} className="close-btn-secondary">
                    ✕ Close
                  </button>
                </div>
              </div>
              
              <div className="image-container">
                <img 
                  src={imageSource === 'generate' ? generatedImage.imageUrl : uploadedImage.url}
                  alt={imageSource === 'generate' ? 'Generated thought' : 'Uploaded image'}
                  className="generated-image"
                />
              </div>
              
              <div className="image-info">
                {imageSource === 'generate' && generatedImage && (
                  <p><strong>Search Query:</strong> {generatedImage.searchQuery}</p>
                )}
                {imageSource === 'upload' && uploadedImage && (
                  <p><strong>File Name:</strong> {uploadedImage.name}</p>
                )}
                <p><strong>Thought:</strong> {thought}</p>
                {author && <p><strong>Author:</strong> {author}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageGenerator;

